import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import './Dashboard.css';

const Dashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    clientes: 0,
    productos: 0,
    presupuestosPendientes: 0,
    pedidosARevisar: 0,
    pedidosVencidos: 0,
    movimientosStockHoy: 0,
    ingresosStockHoy: 0,
    egresosStockHoy: 0
  });
  const [pedidosRecientes, setPedidosRecientes] = useState([]);
  const [movimientosStockRecientes, setMovimientosStockRecientes] = useState([]);

  // Cargar estadísticas
  useEffect(() => {
    cargarEstadisticas();
  }, []);

  const cargarEstadisticas = async () => {
    try {
      setLoading(true);
      const response = await api.get('/panel-control/estadisticas');
      
      if (response.success) {
        const data = response.data;
        setStats({
          clientes: data.clientes,
          productos: data.productos,
          presupuestosPendientes: data.presupuestosPendientes,
          pedidosARevisar: data.pedidosARevisar,
          pedidosVencidos: data.pedidosVencidos,
          movimientosStockHoy: data?.stockResumenHoy?.movimientosHoy || 0,
          ingresosStockHoy: data?.stockResumenHoy?.ingresosHoy || 0,
          egresosStockHoy: data?.stockResumenHoy?.egresosHoy || 0
        });
        setPedidosRecientes(data.pedidosRecientes);
        setMovimientosStockRecientes(data.movimientosStockRecientes || []);
      }
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'en_proceso': return '#28a745';
      case 'a_revisar': return '#ffc107';
      case 'vencido': return '#dc3545';
      case 'finalizado': return '#6c757d';
      default: return '#007bff';
    }
  };

  const getTipoMovimientoColor = (tipo) => {
    if (tipo === 'INGRESO') return '#1f8f4a';
    if (tipo === 'EGRESO') return '#c0392b';
    return '#e67e22';
  };

  const getTipoMovimientoLabel = (tipo) => {
    if (tipo === 'INGRESO') return 'INGRESO';
    if (tipo === 'EGRESO') return 'EGRESO';
    return 'AJUSTE';
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Panel Principal</h1>
        <p>
          {user?.rol === 'admin' 
            ? 'Resumen general de todas las sucursales' 
            : `Resumen de sucursal: ${user?.sucursal?.toUpperCase()}`}
        </p>
      </div>

      {loading ? (
        <LoadingSpinner label="Cargando estadisticas..." />
      ) : (
        <>
          {/* Estadísticas rápidas */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-info">
                <h3>{stats.clientes}</h3>
                <p>Clientes</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">📦</div>
              <div className="stat-info">
                <h3>{stats.productos}</h3>
                <p>Stock</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">💰</div>
              <div className="stat-info">
                <h3>{stats.presupuestosPendientes}</h3>
                <p>Presupuestos Pendientes</p>
              </div>
            </div>

            <div className="stat-card warning">
              <div className="stat-icon">⚠️</div>
              <div className="stat-info">
                <h3>{stats.pedidosARevisar}</h3>
                <p>Pedidos a Revisar</p>
              </div>
            </div>

            <div className="stat-card alert">
              <div className="stat-icon">🔴</div>
              <div className="stat-info">
                <h3>{stats.pedidosVencidos}</h3>
                <p>Pedidos Vencidos</p>
              </div>
            </div>

          </div>

          {/* Sección de pedidos recientes */}
          <div className="dashboard-section">
            <h2>Pedidos Recientes</h2>
            {pedidosRecientes.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                No hay pedidos recientes
              </p>
            ) : (
              <div className="pedidos-list">
                {pedidosRecientes.map(pedido => (
                  <div key={pedido._id} className="pedido-item">
                    <div className="pedido-cliente">
                      <h4>{pedido.cliente}</h4>
                    </div>

                    <div className="pedido-meta">
                      <div className="pedido-meta-item">
                        <span className="meta-label">Sucursal:</span>
                        <span className="meta-value">{pedido.sucursal?.toUpperCase()}</span>
                      </div>
                      <div className="pedido-meta-item">
                        <span className="meta-label">Fecha:</span>
                        <span className="meta-value">{new Date(pedido.fecha).toLocaleDateString()}</span>
                      </div>
                      <div className="pedido-meta-item">
                        <span className="meta-label">Límite:</span>
                        <span className="meta-value">
                          {pedido.fechaLimite ? new Date(pedido.fechaLimite).toLocaleDateString() : '-'}
                        </span>
                      </div>
                    </div>

                    <div className="pedido-estado">
                      <span 
                        className="estado-badge"
                        style={{ backgroundColor: getEstadoColor(pedido.estado) }}
                      >
                        {pedido.estado.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="dashboard-section">
            <h2>Resumen de Registro de Stock</h2>
            <p className="movimientos-resumen-texto">
              Hoy: {stats.movimientosStockHoy} movimientos | +{stats.ingresosStockHoy} ingresos | -{stats.egresosStockHoy} egresos
            </p>

            {movimientosStockRecientes.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                No hay movimientos recientes de stock
              </p>
            ) : (
              <div className="movimientos-list">
                {movimientosStockRecientes.map((mov) => (
                  <div key={mov._id} className="movimiento-item">
                    <div className="movimiento-producto">
                      <h4>{mov?.producto?.modelo || 'Sin modelo'}</h4>
                      <p>
                        {mov?.producto?.categoria || '-'}
                        {mov?.producto?.variante?.sku ? ` | SKU: ${mov.producto.variante.sku}` : ''}
                        {mov?.producto?.variante?.madera ? ` | ${mov.producto.variante.madera}` : ''}
                        {mov?.producto?.variante?.lustre ? ` | ${mov.producto.variante.lustre}` : ''}
                      </p>
                    </div>

                    <div className="movimiento-meta">
                      <div className="movimiento-meta-item">
                        <span className="meta-label">Sucursal:</span>
                        <span className="meta-value">{String(mov.sucursal || '-').toUpperCase()}</span>
                      </div>
                      <div className="movimiento-meta-item">
                        <span className="meta-label">Fecha:</span>
                        <span className="meta-value">{new Date(mov.fecha).toLocaleString('es-AR')}</span>
                      </div>
                      <div className="movimiento-meta-item">
                        <span className="meta-label">Origen:</span>
                        <span className="meta-value">{mov.origen || '-'}</span>
                      </div>
                    </div>

                    <div className="movimiento-tipo-wrap">
                      <span
                        className="movimiento-tipo"
                        style={{ backgroundColor: getTipoMovimientoColor(mov.tipoMovimiento) }}
                      >
                        {getTipoMovimientoLabel(mov.tipoMovimiento)}
                      </span>
                      <span className="movimiento-cantidad">{mov.cantidad}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;